import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "../../../lib/toast";
import * as XLSX from "xlsx";
import { useSimplAI } from "../../../../imports/useSimpleAI";
import { spendCleaningService } from "../../../../services/spendCleaningService";
import { spendSupplierConsolidationService } from "../../../../services/spendSupplierConsolidationService";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "../../../components/ui/pagination";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../../components/ui/tooltip";

type DataCleaningStepProps = {
  data: Record<string, any>[];
  originalData?: Record<string, any>[];
  onNext: (data: Record<string, any>[]) => void;
  onBack: () => void;
  onCancel: () => void;
};

type TableRow = {
  [key: string]: any;
  __sourceRow?: number;
  __runStatus?: string;
  __warningCount?: number;
  __warningMessage?: string;
  run_id?: string;
  id?: string;
};

const INTERNAL_COLUMNS = [
  "run_id",
  "__sourceRow",
  "__runStatus",
  "__warningCount",
  "__warningMessage",
  "confidence_score",
  "confidence_score_reasoning",
  "action_taken",
  "action",
  "id",
];

const SUPPLIER_CONSOLIDATION_BATCH_SIZE = 100;

const chunkRows = <T,>(rows: T[], size: number): T[][] => {
  const chunks: T[][] = [];
  for (let i = 0; i < rows.length; i += size) {
    chunks.push(rows.slice(i, i + size));
  }
  return chunks;
};

const isObjectRowArray = (value: unknown): value is TableRow[] => {
  return (
    Array.isArray(value) &&
    value.every(
      (item) => !!item && typeof item === "object" && !Array.isArray(item)
    )
  );
};

const normalizeRowsFromSimplAI = (response: any): TableRow[] => {
  if (typeof response === "string") {
    try {
      return normalizeRowsFromSimplAI(JSON.parse(response));
    } catch {
      return [];
    }
  }

  if (isObjectRowArray(response?.data?.output?.data)) {
    return response.data.output.data;
  }

  if (isObjectRowArray(response?.data?.output)) {
    return response.data.output;
  }

  if (isObjectRowArray(response?.data?.output?.foreach)) {
    return response.data.output.foreach;
  }

  if (isObjectRowArray(response?.data?.input_data)) {
    return response.data.input_data;
  }

  if (isObjectRowArray(response?.data)) {
    return response.data;
  }

  if (isObjectRowArray(response?.output?.data)) {
    return response.output.data;
  }

  if (isObjectRowArray(response?.output)) {
    return response.output;
  }

  if (isObjectRowArray(response)) {
    return response;
  }

  return [];
};

const convertValue = (value: any) => {
  if (value === null || value === undefined) return "";

  if (value instanceof Date) {
    return value.toISOString();
  }

  if (typeof value === "string") {
    return value.trim();
  }

  if (typeof value === "number" || typeof value === "boolean") {
    return value;
  }

  return value;
};

const getColumnsFromRows = (rows: TableRow[]) => {
  const columnSet = new Set<string>();

  rows.forEach((row) => {
    Object.keys(row || {}).forEach((key) => {
      if (!key.startsWith("__") && key !== "run_id" && key !== "id") {
        columnSet.add(key);
      }
    });
  });

  return Array.from(columnSet);
};

export function DataCleaningStep({
  data,
  originalData = [],
  onNext,
  onBack,
  onCancel,
}: DataCleaningStepProps) {
  const { runSimplAI, loading: simplAILoading } = useSimplAI();
  const [qcSidebarCollapsed, setQcSidebarCollapsed] = useState(false);
  // Hook's loading flips false when the first parallel batch finishes, so track batches separately.
  const [batchProcessing, setBatchProcessing] = useState(false);
  const [batchProgress, setBatchProgress] = useState({ done: 0, total: 0 });
  const loading = simplAILoading || batchProcessing;
  //const { cleanedExcelData, simplAIReadyData } = useExcelData();

  const [tableData, setTableData] = useState<TableRow[]>([]);
  const [openAICleanedRows, setOpenAICleanedRows] = useState<Record<string, any>[]>([]);

  useEffect(() => {
    loadOpenAICleanedData();
    console.log("✅ DATA CLEANING STEP INPUT DATA:", data);
   // console.log("✅ CLEANED EXCEL DATA FROM CONTEXT:", cleanedExcelData);
    //console.log("✅ SIMPLAI READY DATA FROM CONTEXT:", simplAIReadyData);

    if (Array.isArray(data) && data.length > 0) {
      setTableData(data);
      return;
    }

    /* if (Array.isArray(cleanedExcelData) && cleanedExcelData.length > 0) {
      setTableData(cleanedExcelData);
      return;
    } */

    setTableData([]);
  }, [data]);


  const columns = getColumnsFromRows(tableData);

  const totalRecords = tableData.length;

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    setPage(1);
  }, [tableData.length, rowsPerPage]);

  const totalPages = Math.max(1, Math.ceil(tableData.length / rowsPerPage));
  const safePage = Math.min(page, totalPages);
  const pageStartIndex = (safePage - 1) * rowsPerPage;
  const pageRows = tableData.slice(pageStartIndex, pageStartIndex + rowsPerPage);

  const getOriginalRow = (row: TableRow, index: number) =>
    originalData[typeof row.__sourceRow === "number" ? row.__sourceRow - 1 : index];

  const isCorrectedCell = (
    originalRow: Record<string, any> | undefined,
    column: string,
    cleanedValue: unknown
  ) => {
    if (!originalRow || !Object.prototype.hasOwnProperty.call(originalRow, column)) {
      return false;
    }

    const original = String(originalRow[column] ?? "").trim();
    const cleaned = String(cleanedValue ?? "").trim();

    if (original === cleaned) return false;

    const numericPattern = /^-?\d+(\.\d+)?$/;
    if (numericPattern.test(original) && numericPattern.test(cleaned)) {
      // Only rounding/formatting differences (e.g. 7.50 -> 7.5, 1.234 -> 1.23) are not corrections.
      return Math.round(Number(original) * 100) !== Math.round(Number(cleaned) * 100);
    }

    return true;
  };

  // ✅ Function to load OpenAI cleaned data
  const loadOpenAICleanedData = async () => {
    try {
      // Uncomment and update with your actual OpenAI fetch function
       const latestRecord = await spendCleaningService.getLatestRecord();
       const cleanedData = latestRecord?.cleaned_data;
       const openAIResponse =
         typeof cleanedData === "string" ? JSON.parse(cleanedData) : cleanedData;
       
       if (Array.isArray(openAIResponse?.rows)) {
         const rows = openAIResponse.rows.map((row: Record<string, unknown>, index: number) => ({
           __sourceRow: index + 1,
           run_id: `openai-${index + 1}`,
           __runStatus: "completed",
           __warningCount: 0,
           __warningMessage: "",
           ...row,
           }));
           setOpenAICleanedRows(rows);
           console.log("✅ LATEST CLEANED DATA LOADED:", rows.length);
         }
    } catch (err) {
      console.error("❌ Failed to load latest cleaned data:", err);
    }
  };

/*   const buildSupplierConsolidationPayload = () => {
    
      IMPORTANT:
      Supplier Consolidation / Tool 57 expects readable headers:
      "Supplier Name", "Supplier Region", "Line Item Description", etc.

      Do NOT send simplAIReadyData here.
      simplAIReadyData has underscore headers:
      Supplier_Name, Supplier_Region, Line_Item_Description.
    
    
    const sourceRows =
      Array.isArray(cleanedExcelData) && cleanedExcelData.length > 0
        ? cleanedExcelData
        : tableData;

    if (!sourceRows.length) {
      throw new Error("No cleaned data found for Supplier Consolidation");
    }

    const cleanedRows = sourceRows.map((row) => {
      const newRow: Record<string, any> = {};

      Object.keys(row || {}).forEach((key) => {
        if (INTERNAL_COLUMNS.includes(key)) return;
        if (key.startsWith("__")) return;

        newRow[key] = convertValue(row[key]);
      });

      return newRow;
    });

    const payload = {
      data: {
        input_data: cleanedRows,
      },
    };

    console.log("✅ TOOL 17 PAYLOAD ROW COUNT:", cleanedRows.length);
    console.log("✅ TOOL 17 PAYLOAD FIRST ROW:", cleanedRows[0]);
    console.log("✅ TOOL 17 PAYLOAD:", payload);

    return payload;
  }; */

  const handleDownloadUniqueSuppliers = () => {
    if (!tableData.length) {
      toast.error("No data available to download");
      return;
    }

    const supplierColumns = [
      "Supplier Name",
      "Supplier_Name",
      "supplier_name",
      "Vendor Name",
      "Vendor_Name",
      "vendor_name",
    ];

    const supplierColumn = supplierColumns.find((column) =>
      columns.includes(column)
    );

    if (!supplierColumn) {
      toast.error("Supplier column not found");
      return;
    }

    const uniqueSuppliers = new Map<string, TableRow>();

    tableData.forEach((row) => {
      const supplier = String(row[supplierColumn] ?? "").trim();

      if (!supplier) return;

      if (!uniqueSuppliers.has(supplier.toLowerCase())) {
        uniqueSuppliers.set(supplier.toLowerCase(), row);
      }
    });

    const exportRows = Array.from(uniqueSuppliers.values()).map((row) => {
      const cleanRow: Record<string, any> = {};

      Object.keys(row || {}).forEach((key) => {
        if (!INTERNAL_COLUMNS.includes(key) && !key.startsWith("__")) {
          cleanRow[key] = row[key];
        }
      });

      return cleanRow;
    });

    if (!exportRows.length) {
      toast.error("No supplier rows found to export");
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(exportRows);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "Unique Suppliers");
    XLSX.writeFile(workbook, "Unique_Suppliers.xlsx");

    toast.success(`Downloaded ${exportRows.length} unique suppliers`);
  };

  const handleNext = async () => {
    setBatchProcessing(true);

    try {
      const savedRecord = await spendSupplierConsolidationService.getLatestRecord();
      const savedRows = normalizeRowsFromSimplAI(savedRecord?.output_data);

      if (
        String(savedRecord?.processing_status || "").toUpperCase() === "COMPLETED" &&
        savedRows.length > 0
      ) {
        toast.success(`Successfully processed ${savedRows.length} suppliers`);
        onNext(savedRows);
        return;
      }

      // ✅ STEP 1: Use OpenAI cleaned rows if available, otherwise use table data
      const sourceRows = openAICleanedRows.length > 0 ? openAICleanedRows : tableData;

      if (!sourceRows.length) {
        toast.error("No cleaned data found for Supplier Consolidation");
        return;
      }

      console.log("✅ USING SOURCE ROWS:", sourceRows.length);
      console.log("✅ FIRST SOURCE ROW:", sourceRows[0]);

      // ✅ STEP 2: Build supplier consolidation payload
      const payloadRows = sourceRows.map((row) => {
        const newRow: Record<string, any> = {};

        Object.keys(row || {}).forEach((key) => {
          // Filter out internal columns
          if (INTERNAL_COLUMNS.includes(key)) return;
          if (key.startsWith("__")) return;

          // Convert values
          const value = row[key];
          newRow[key] =
            value === null || value === undefined
              ? ""
              : typeof value === "string"
              ? value.trim()
              : value;
        });

        return newRow;
      });

      const batches = chunkRows(payloadRows, SUPPLIER_CONSOLIDATION_BATCH_SIZE);

      console.log("✅ SUPPLIER CONSOLIDATION PAYLOAD ROW COUNT:", payloadRows.length);
      console.log("✅ SUPPLIER CONSOLIDATION BATCH COUNT:", batches.length);

      setBatchProgress({ done: 0, total: batches.length });

      // ✅ STEP 3: Send each batch to SimplAI (Tool 57) in parallel
      const batchResults = await Promise.all(
        batches.map(async (batchRows, batchIndex) => {
          const result = await runSimplAI({ data: { input_data: batchRows } }, 57, {
            onPostResponse: async ({
              cId,
              mId,
              postResponse,
              simplaiPayload,
              inputData,
              isStandalone,
            }: any) => {
              await spendSupplierConsolidationService.saveSupplierConsolidationRecord({
                conversation_id: cId,
                message_id: mId,
                input_data: {
                  input_data: inputData,
                  simplai_post_payload: simplaiPayload,
                  simplai_post_response: postResponse,
                },
                created_by: "system",
                is_standalone: isStandalone,
              });
            },
            onPollComplete: async ({ cId, mId, data, trace_id }: any) => {
              await spendSupplierConsolidationService.updateSupplierConsolidationRecord({
                conversation_id: cId,
                message_id: mId,
                request_id: trace_id || `req-${Date.now()}`,
                output_data: data,
                processing_status: "COMPLETED",
              });
            },
          });

          const rows = normalizeRowsFromSimplAI(result);
          console.log(
            `✅ TOOL 57 BATCH ${batchIndex + 1}/${batches.length} OUTPUT ROWS:`,
            rows.length
          );
          setBatchProgress((prev) => ({ ...prev, done: prev.done + 1 }));
          return rows;
        })
      );

      // Promise.all preserves batch order, so the combined output matches input order.
      const normalizedRows = batchResults.flat();

      console.log("✅ TOOL 57 OUTPUT ROW COUNT:", normalizedRows.length);
      console.log("✅ TOOL 57 OUTPUT FIRST ROW:", normalizedRows[0]);

      if (!normalizedRows.length) {
        toast.error("Invalid SimplAI response");
        return;
      }

      toast.success(`Successfully processed ${normalizedRows.length} suppliers`);
      onNext(normalizedRows);
    } catch (err: any) {
      console.error("❌ SUPPLIER CONSOLIDATION ERROR:", err);
      toast.error(err?.message || "Supplier Consolidation failed");
    } finally {
      setBatchProcessing(false);
    }
  };

  return (
    <div className="setup-screen pad-0 bg-transparent">
      {loading && (
        <div className="ai-processing-overlay">
          <div className="loader_pulse">
            <div />
            <div />
            <div />
            <div />
            <div />
          </div>
          <div className="ai-processing-overlay__label">
            AI is processing your data, please wait...
            {batchProgress.total > 1 &&
              ` (${batchProgress.done}/${batchProgress.total} batches done)`}
          </div>
        </div>
      )}

      <div className="bg-white border border-color-d4 radius-10">
        <div className="setup-form-header border-bottom pad-x-15 pad-y-10">
          <div className="setup-form-title">Data quality metrics</div>
          <div className="setup-form-desc">
            Updated after every cleaning run.
          </div>
        </div>

        <div className="top-stats pad-x-15 pad-y-10">
          <div className="stat-card bg-fcfbfd">
            <div className="stat-title">Record Processed</div>
            <div className="stat-value">{totalRecords.toLocaleString()}</div>
          </div>

          <div className="stat-card bg-fcfbfd">
            <div className="stat-title">Exception Pending</div>
            <div className="stat-value">0</div>
          </div>

          <div className="stat-card bg-fcfbfd">
            <div className="stat-title">AI Auto-Cleaned</div>
            <div className="stat-value">100%</div>
          </div>

          <div className="stat-card bg-fcfbfd">
            <div className="stat-title">Total Suppliers</div>
            <div className="stat-value">12,481</div>
          </div>
        </div>
      </div>

      <div className={`qc-layout qc-layout--wide${qcSidebarCollapsed ? " qc-layout--sidebar-collapsed" : ""}`}>
        <div className={`qc-sidebar${qcSidebarCollapsed ? " is-collapsed" : ""}`}>
          <div className="border-bottom pad-12 qc-sidebar-head">
            {!qcSidebarCollapsed && (
              <div>
                <div className="sidebar-title">Exceptions queue</div>
                <div className="sidebar-sub">Click to filter the grid</div>
              </div>
            )}
            <button
              type="button"
              className="qc-sidebar-toggle"
              onClick={() => setQcSidebarCollapsed((value) => !value)}
              aria-label={qcSidebarCollapsed ? "Expand exceptions queue" : "Collapse exceptions queue"}
              title={qcSidebarCollapsed ? "Expand" : "Collapse"}
            >
              {qcSidebarCollapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
            </button>
          </div>

          {!qcSidebarCollapsed && (
            <div className="sidebar-list pad-12">
              <div className="sidebar-item active">
                <div className="sidebar-left">
                  <span className="dot warning" />
                  <span className="label">Currency Mismatch</span>
                </div>
                <span className="count">412</span>
              </div>

              <div className="sidebar-item">
                <div className="sidebar-left">
                  <span className="dot neutral" />
                  <span className="label">Invalid invoice IDs</span>
                </div>
                <span className="count">82</span>
              </div>

              <div className="sidebar-item">
                <div className="sidebar-left">
                  <span className="dot warning" />
                  <span className="label">Duplicate suppliers</span>
                </div>
                <span className="count">2441</span>
              </div>
            </div>
          )}
        </div>

        <div className="qc-main">
          <div className="qc-filters">
            <div className="filter-title">QC FILTERS</div>

            <div className="filter-tabs">
              <span className="tab active">All</span>
              <span className="tab">Agent processed</span>
              <span className="tab">Human Reviewed</span>
              <span className="tab">Pending Approval</span>
              <span className="tab">Rejected</span>
            </div>

            <div className="filter-sort">
              Sort:
              <select>
                <option>Confidence</option>
              </select>
            </div>
          </div>

          <div className="qc-table-card">
            <div className="table-header">
              <div>
                Cleaned procurement data{" "}
                <span className="badge">{tableData.length} rows</span>
              </div>

              <div className="table-actions">
                <button className="table-action-btn btn-success">Approve</button>
                <button className="table-action-btn btn-danger">Reject</button>
                <button className="table-action-btn btn-outline">Edit</button>
                <button className="table-action-btn btn-outline">Merge</button>
                <button className="table-action-btn btn-outline">Delete</button>
              </div>
            </div>

            {tableData.length === 0 ? (
              <div style={{ padding: 20 }}>
                ⚠️ No data received from previous step
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table className="qc-table">
                  <thead>
                    <tr>
                      {columns.map((column) => (
                        <th key={column}>{column}</th>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {pageRows.map((row, pageRowIndex) => {
                      const rowIndex = pageStartIndex + pageRowIndex;
                      const originalRow = getOriginalRow(row, rowIndex);

                      return (
                        <tr
                          key={rowIndex}
                          style={{
                            height: "40px",
                            background: "transparent",
                          }}
                        >
                          {columns.map((column) => {
                            const value = String(row[column] ?? "");
                            const corrected = isCorrectedCell(originalRow, column, row[column]);

                            return (
                              <td
                                key={column}
                                title={corrected ? undefined : value}
                                style={{
                                  maxWidth: "180px",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {corrected ? (
                                  <span className="qc-cell--corrected">
                                    <span className="qc-cell--corrected__text">{value}</span>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <span
                                          className="qc-cell--corrected__dot"
                                          aria-label="Corrected by agent"
                                        />
                                      </TooltipTrigger>
                                      <TooltipContent
                                        side="bottom"
                                        align="start"
                                        sideOffset={10}
                                        alignOffset={14}
                                        className="qc-cell-tooltip"
                                      >
                                        Corrected by agent. Original: {String(originalRow?.[column] ?? "")}
                                      </TooltipContent>
                                    </Tooltip>
                                  </span>
                                ) : (
                                  value
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {tableData.length > 0 && (
              <div className="cmw-param-card__pagination">
                {totalPages > 1 && (
                  <Pagination>
                    <PaginationContent>
                      <PaginationItem>
                        <PaginationPrevious
                          href="#"
                          onClick={(event) => {
                            event.preventDefault();
                            setPage((current) => Math.max(1, current - 1));
                          }}
                          aria-disabled={safePage === 1}
                          tabIndex={safePage === 1 ? -1 : 0}
                        />
                      </PaginationItem>

                      {Array.from({ length: totalPages }, (_, index) => index + 1).map((pageNumber) => (
                        <PaginationItem key={pageNumber}>
                          <PaginationLink
                            href="#"
                            isActive={pageNumber === safePage}
                            onClick={(event) => {
                              event.preventDefault();
                              setPage(pageNumber);
                            }}
                          >
                            {pageNumber}
                          </PaginationLink>
                        </PaginationItem>
                      ))}

                      <PaginationItem>
                        <PaginationNext
                          href="#"
                          onClick={(event) => {
                            event.preventDefault();
                            setPage((current) => Math.min(totalPages, current + 1));
                          }}
                          aria-disabled={safePage === totalPages}
                          tabIndex={safePage === totalPages ? -1 : 0}
                        />
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                )}
                <div className="cmw-param-card__pagination-size">
                  <label htmlFor="data-cleaning-rows-per-page">Rows per page</label>
                  <select
                    id="data-cleaning-rows-per-page"
                    value={rowsPerPage}
                    onChange={(event) => setRowsPerPage(Number(event.target.value))}
                  >
                    {[10, 20, 50, 100].map((count) => (
                      <option key={count} value={count}>
                        {count}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="wizard-action-bar">
        <button
          className="action-bar__cancel btn btn--outline"
          onClick={onCancel}
        >
          Cancel
        </button>

        <button
          className="btn btn--outline m-left-auto"
          onClick={handleDownloadUniqueSuppliers}
        >
          Download Excel (Unique Suppliers)
        </button>

        <button className="btn btn--outline" onClick={onBack}>
          <ChevronLeft size={14} /> Back
        </button>

        <button
          className="btn btn--primary"
          onClick={handleNext}
          disabled={loading}
        >
          {loading ? "Processing..." : "Supplier Consolidation"}{" "}
          <ChevronRight size={14} />
        </button>
      </div>
    </div>
  );
}

export default DataCleaningStep;